import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildEmptyMarketingEmailProviderConnectionForm,
  buildMarketingEmailProviderConnectionPayloadFromForm,
  buildMarketingEmailSyncDryRunPayload,
  buildMarketingEmailSyncJobPayload,
  mapMarketingEmailProviderConnectionListResponseToDto,
  mapMarketingEmailProviderConnectionResponseToDto,
  mapMarketingEmailSyncJobListResponseToDto,
  validateMarketingEmailProviderConnectionPayload,
} from '../dto/marketing-email-providers.dto.js';
import {
  buildMarketingEmailProviderConnectionRows,
  buildMarketingEmailSyncJobRows,
} from '../model/marketing-email-providers.model.js';

const EMPTY_FILTERS = { countries: [], profileStatuses: [], languages: [] };
const SYNC_JOB_POLL_INTERVAL_MS = 2000;
const SYNC_JOB_POLL_ATTEMPTS = 15;
const ACTIVE_SYNC_JOB_STATUSES = new Set(['pending', 'running']);

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean);
  }

  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function mapCountryOptions(payload) {
  const raw = payload?.countries ?? payload?.items ?? payload?.data ?? payload;
  const list = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? Object.values(raw) : []);
  const options = list
    .map((item) => {
      if (typeof item === 'string') {
        const value = item.trim().toUpperCase();
        return value ? { value, label: value } : null;
      }

      if (!item || typeof item !== 'object') {
        return null;
      }

      const value = String(item.code || item.value || item.country || '').trim().toUpperCase();
      if (!value) {
        return null;
      }

      const name = String(item.name || item.label || '').trim();
      return { value, label: name ? `${value} - ${name}` : value };
    })
    .filter(Boolean);

  const uniq = new Map();
  options.forEach((option) => uniq.set(option.value, option));
  return Array.from(uniq.values()).sort((left, right) => left.value.localeCompare(right.value));
}

function connectionToForm(connection) {
  return {
    provider: connection?.provider || 'brevo',
    displayName: connection?.displayName || '',
    apiKey: '',
    apiBaseUrl: connection?.apiBaseUrl || '',
    defaultListId: connection?.defaultListId || '',
    enabled: Boolean(connection?.enabled ?? true),
  };
}

function filtersPayload(filters) {
  return {
    countries: normalizeList(filters.countries),
    profileStatuses: normalizeList(filters.profileStatuses),
    languages: normalizeList(filters.languages),
  };
}

function isActiveSyncJob(job) {
  return ACTIVE_SYNC_JOB_STATUSES.has(String(job?.status || '').trim());
}

export function useMarketingEmailProviders() {
  const { token } = useAuth();
  const syncPollTimeoutRef = useRef(null);
  const [connections, setConnections] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [selectedUid, setSelectedUid] = useState('');
  const [form, setForm] = useState(buildEmptyMarketingEmailProviderConnectionForm());
  const [countryOptions, setCountryOptions] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [targetListId, setTargetListId] = useState('');
  const [dryRun, setDryRun] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const clearSyncJobPoll = useCallback(() => {
    if (syncPollTimeoutRef.current) {
      window.clearTimeout(syncPollTimeoutRef.current);
      syncPollTimeoutRef.current = null;
    }
  }, []);

  const reloadSyncJobs = useCallback(async () => {
    const response = await adminApi.getMarketingEmailSyncJobs(token);
    const nextJobs = mapMarketingEmailSyncJobListResponseToDto(response).jobs;
    setJobs(nextJobs);
    return nextJobs;
  }, [token]);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const [connectionsResponse, jobsResponse] = await Promise.all([
        adminApi.getMarketingEmailProviderConnections(token),
        adminApi.getMarketingEmailSyncJobs(token),
      ]);
      const nextConnections = mapMarketingEmailProviderConnectionListResponseToDto(connectionsResponse).connections;
      const nextJobs = mapMarketingEmailSyncJobListResponseToDto(jobsResponse).jobs;
      setConnections(nextConnections);
      setJobs(nextJobs);
      if (!selectedUid && nextConnections[0]?.uid) {
        setSelectedUid(nextConnections[0].uid);
        setForm(connectionToForm(nextConnections[0]));
      }
      return { connections: nextConnections, jobs: nextJobs };
    } catch (requestError) {
      setConnections([]);
      setJobs([]);
      setError(requestError.message || 'Failed to load provider sync');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [selectedUid, token]);

  const scheduleSyncJobPoll = useCallback((jobUid) => {
    clearSyncJobPoll();
    let attemptsRemaining = SYNC_JOB_POLL_ATTEMPTS;

    const poll = async () => {
      try {
        const nextJobs = await reloadSyncJobs();
        const watchedJob = (jobUid ? nextJobs.find((job) => job.uid === jobUid) : null) || nextJobs[0] || null;
        attemptsRemaining -= 1;

        if (isActiveSyncJob(watchedJob) && attemptsRemaining > 0) {
          syncPollTimeoutRef.current = window.setTimeout(poll, SYNC_JOB_POLL_INTERVAL_MS);
          return;
        }

        syncPollTimeoutRef.current = null;
      } catch (requestError) {
        syncPollTimeoutRef.current = null;
        setError(requestError.message || 'Failed to refresh sync jobs');
      }
    };

    syncPollTimeoutRef.current = window.setTimeout(poll, SYNC_JOB_POLL_INTERVAL_MS);
  }, [clearSyncJobPoll, reloadSyncJobs]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => () => clearSyncJobPoll(), [clearSyncJobPoll]);

  useEffect(() => {
    let isCancelled = false;

    adminApi.getCountryDictionary(token)
      .then((response) => {
        if (!isCancelled) {
          setCountryOptions(mapCountryOptions(response));
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setCountryOptions([]);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const selectConnection = useCallback((uid) => {
    const connection = connections.find((item) => item.uid === uid) || null;
    setSelectedUid(connection?.uid || '');
    setForm(connection ? connectionToForm(connection) : buildEmptyMarketingEmailProviderConnectionForm());
    setFormErrors({});
    setDryRun(null);
    setError('');
    setSuccess('');
  }, [connections]);

  const startNewConnection = useCallback(() => {
    setSelectedUid('');
    setForm(buildEmptyMarketingEmailProviderConnectionForm());
    setFormErrors({});
    setError('');
    setSuccess('');
  }, []);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: key === 'enabled' ? Boolean(value) : value }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const onFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: normalizeList(value) }));
  }, []);

  const saveConnection = useCallback(async (event) => {
    event.preventDefault();
    const payload = buildMarketingEmailProviderConnectionPayloadFromForm(form);
    const nextErrors = validateMarketingEmailProviderConnectionPayload(payload);

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = selectedUid
        ? await adminApi.updateMarketingEmailProviderConnection(token, selectedUid, payload)
        : await adminApi.createMarketingEmailProviderConnection(token, payload);
      const connection = mapMarketingEmailProviderConnectionResponseToDto(response);
      setSelectedUid(connection.uid);
      setForm(connectionToForm(connection));
      setSuccess('Provider connection saved.');
      await load();
    } catch (requestError) {
      setError(requestError.message || 'Failed to save provider connection');
    } finally {
      setIsSaving(false);
    }
  }, [form, load, selectedUid, token]);

  const testConnection = useCallback(async () => {
    if (!selectedUid) {
      return;
    }

    setIsTesting(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.testMarketingEmailProviderConnection(token, selectedUid);
      setSuccess('Provider connection test passed.');
    } catch (requestError) {
      setError(requestError.message || 'Provider connection test failed');
    } finally {
      setIsTesting(false);
    }
  }, [selectedUid, token]);

  const runDryRun = useCallback(async () => {
    setIsSyncing(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.dryRunMarketingEmailSync(token, buildMarketingEmailSyncDryRunPayload(filtersPayload(filters)));
      setDryRun(response);
      setSuccess('Audience dry run completed.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to run sync dry run');
    } finally {
      setIsSyncing(false);
    }
  }, [filters, token]);

  const startSyncJob = useCallback(async () => {
    if (!selectedUid) {
      return;
    }

    setIsSyncing(true);
    setError('');
    setSuccess('');

    try {
      const response = await adminApi.createMarketingEmailSyncJob(token, buildMarketingEmailSyncJobPayload({
        connectionUid: selectedUid,
        targetListId,
        audienceFilters: filtersPayload(filters),
      }));
      const jobUid = String(response?.job?.uid || response?.uid || '').trim();
      const nextState = await load();
      const watchedJob = (jobUid ? nextState?.jobs?.find((job) => job.uid === jobUid) : null) || nextState?.jobs?.[0] || null;
      if (isActiveSyncJob(watchedJob) || (jobUid && !watchedJob)) {
        scheduleSyncJobPoll(jobUid);
      }
      setSuccess('Contact sync job created.');
    } catch (requestError) {
      setError(requestError.message || 'Failed to create sync job');
    } finally {
      setIsSyncing(false);
    }
  }, [filters, load, scheduleSyncJobPoll, selectedUid, targetListId, token]);

  const connectionRows = useMemo(() => buildMarketingEmailProviderConnectionRows(connections), [connections]);
  const jobRows = useMemo(() => buildMarketingEmailSyncJobRows(jobs), [jobs]);

  return {
    connectionRows,
    jobRows,
    selectedUid,
    form,
    countryOptions,
    filters,
    targetListId,
    dryRun,
    isLoading,
    isSaving,
    isTesting,
    isSyncing,
    error,
    success,
    formErrors,
    onFormChange,
    onFilterChange,
    setTargetListId,
    selectConnection,
    startNewConnection,
    saveConnection,
    testConnection,
    runDryRun,
    startSyncJob,
    reload: load,
  };
}
